import {
  useQueryClient,
  useQueryErrorResetBoundary,
} from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';

import {
  CategoryListDataError,
  logCategoryListError,
} from '@/utils/booking/categoryListErrors';

import type { UseCategoryListRetryResult } from './types';

export function useCategoryListRetry(): UseCategoryListRetryResult {
  const queryClient = useQueryClient();
  const { reset } = useQueryErrorResetBoundary();
  const listErrorRef = useRef<unknown>(null);
  const isRetryingList = useRef(false);
  const [breadcrumbResetKey, setBreadcrumbResetKey] = useState(0);

  const handleListError = (error: unknown) => {
    listErrorRef.current = error;
    logCategoryListError(error);
  };

  const handleListReset = () => {
    const error = listErrorRef.current;
    listErrorRef.current = null;
    if (error instanceof CategoryListDataError) {
      error.staleQueryKeys.forEach((queryKey) => {
        void queryClient.resetQueries({ queryKey, exact: true });
      });
    }
    reset();
    isRetryingList.current = true;
  };

  // The breadcrumb shares the list's queries, so it retries with the list.
  // It waits for the list to load: resetting both while their shared query
  // refetches makes the two Suspense boundaries re-render in a loop.
  const handleListMount = useCallback(() => {
    if (!isRetryingList.current) return;
    isRetryingList.current = false;
    setBreadcrumbResetKey((key) => key + 1);
  }, []);

  return {
    breadcrumbResetKey,
    handleListError,
    handleListReset,
    handleListMount,
  };
}
